import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import axios from 'axios';
import {
  BarChart3,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Equal,
  Filter,
  X,
  Info,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import { Switch } from '../../../../components/ui/switch';
import { cn } from '../../../../lib/utils';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const CONDITION_TYPES = [
  { value: 'field_equals', label: 'Field Equals' },
  { value: 'field_not_equals', label: 'Field Not Equals' },
  { value: 'field_contains', label: 'Field Contains' },
  { value: 'field_is_empty', label: 'Field Is Empty' },
  { value: 'field_is_not_empty', label: 'Field Is Not Empty' },
  { value: 'event_occurred', label: 'Event Occurred' },
  { value: 'tag_added', label: 'Tag Added' },
  { value: 'tag_removed', label: 'Tag Removed' },
  { value: 'status_changed', label: 'Status Changed' },
  { value: 'date_passed', label: 'Date Passed' },
];

const CALCULATION_TYPES = [
  { value: 'add', label: 'Add Points', icon: TrendingUp, color: 'emerald' },
  { value: 'subtract', label: 'Subtract Points', icon: TrendingDown, color: 'red' },
  { value: 'set', label: 'Set Score', icon: Equal, color: 'blue' },
];

const PlatformManageScoringPage = () => {
  const { token } = useAuth();
  const [rules, setRules] = useState([]);
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedObject, setSelectedObject] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [scoringEnabled, setScoringEnabled] = useState(true);
  
  // Form state
  const [formData, setFormData] = useState({
    rule_name: '',
    object_type: '',
    condition_type: 'field_equals',
    condition_config: { field: '', value: '' },
    calculation_type: 'add',
    points: 1,
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [rulesRes, objectsRes] = await Promise.all([
        axios.get(`${API}/platform/settings/data/manage-scoring`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/platform/settings/data/objects`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setRules(rulesRes.data.rules || []);
      setObjects(objectsRes.data.objects || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load scoring rules');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.rule_name || !formData.object_type) {
      toast.error('Rule name and object type are required');
      return;
    }

    try {
      if (editingRule) {
        await axios.put(`${API}/platform/settings/data/manage-scoring/${editingRule.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Rule updated');
      } else {
        await axios.post(`${API}/platform/settings/data/manage-scoring`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Rule created');
      }
      setShowAddModal(false);
      setEditingRule(null);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save rule');
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    
    try {
      await axios.delete(`${API}/platform/settings/data/manage-scoring/${ruleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rule deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete rule');
    }
  };

  const toggleRuleStatus = async (rule) => {
    try {
      await axios.put(`${API}/platform/settings/data/manage-scoring/${rule.id}`, {
        ...rule,
        is_active: !rule.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(rule.is_active ? 'Rule deactivated' : 'Rule activated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update rule');
    }
  };

  const resetForm = () => {
    setFormData({
      rule_name: '',
      object_type: '',
      condition_type: 'field_equals',
      condition_config: { field: '', value: '' },
      calculation_type: 'add',
      points: 1,
      is_active: true
    });
  };

  const openEditModal = (rule) => {
    setEditingRule(rule);
    setFormData({
      rule_name: rule.rule_name,
      object_type: rule.object_type,
      condition_type: rule.condition_type,
      condition_config: rule.condition_config || { field: '', value: '' },
      calculation_type: rule.calculation_type,
      points: rule.points,
      is_active: rule.is_active
    });
    setShowAddModal(true);
  };

  const filteredRules = rules.filter(rule => {
    const matchesSearch = rule.rule_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesObject = !selectedObject || rule.object_type === selectedObject;
    return matchesSearch && matchesObject;
  });

  const getConditionLabel = (type) => {
    return CONDITION_TYPES.find(c => c.value === type)?.label || type;
  };

  const getCalculationConfig = (type) => {
    return CALCULATION_TYPES.find(c => c.value === type) || CALCULATION_TYPES[0];
  };

  const formatCondition = (rule) => {
    const conditionLabel = getConditionLabel(rule.condition_type);
    const config = rule.condition_config || {};
    
    if (config.field && config.value) {
      return `If ${config.field} ${conditionLabel.toLowerCase()} "${config.value}"`;
    }
    return conditionLabel;
  };

  return (
    <div className="min-h-screen bg-[#0B1F18] p-8" data-testid="platform-manage-scoring-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Manage Scoring</h1>
            <p className="text-[#93AE9F] text-sm">
              Configure platform-level engagement and scoring rules
            </p>
          </div>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setEditingRule(null);
            setShowAddModal(true);
          }}
          className="bg-amber-500 hover:bg-amber-600 text-white"
          data-testid="add-rule-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New Rule
        </Button>
      </div>

      {/* Global Toggle */}
      <div className="bg-[rgba(10,38,28,0.6)] border border-[rgba(142,218,182,0.12)] rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn(
              'w-3 h-3 rounded-full',
              scoringEnabled ? 'bg-emerald-500' : 'bg-zinc-500'
            )} />
            <div>
              <h3 className="font-semibold text-white">Engagement Score</h3>
              <p className="text-sm text-[#93AE9F]">
                Enable platform-wide scoring to track agency and user engagement
              </p>
            </div>
          </div>
          <Switch
            checked={scoringEnabled}
            onCheckedChange={setScoringEnabled}
          />
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
          <Input
            placeholder="Search rules..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-zinc-900/50 border-zinc-700 text-white"
          />
        </div>
        <Select
          value={selectedObject || 'all'}
          onValueChange={(v) => setSelectedObject(v === 'all' ? null : v)}
        >
          <SelectTrigger className="w-48 bg-zinc-900/50 border-zinc-700 text-white">
            <Filter className="w-4 h-4 mr-2 text-zinc-500" />
            <SelectValue placeholder="Filter by object" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-700">
            <SelectItem value="all">All Objects</SelectItem>
            {objects.filter(o => o.is_active).map((obj) => (
              <SelectItem key={obj.id} value={obj.name}>{obj.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedObject && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectedObject(null)}
            className="border-zinc-600 text-zinc-400"
          >
            <X className="w-4 h-4 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {/* Rules Table */}
      <div className="bg-[rgba(10,38,28,0.6)] border border-[rgba(142,218,182,0.12)] rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 text-emerald-500 animate-spin" />
          </div>
        ) : filteredRules.length === 0 ? (
          <div className="text-center py-12">
            <BarChart3 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No Scoring Rules</h3>
            <p className="text-[#93AE9F] text-sm mb-4">
              Create scoring rules to track engagement and activity.
            </p>
            <Button
              onClick={() => {
                resetForm();
                setShowAddModal(true);
              }}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create First Rule
            </Button>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-zinc-900/30">
              <tr className="text-left">
                <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Action / Condition</th>
                <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Object</th>
                <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Calculation</th>
                <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgba(142,218,182,0.08)]">
              {filteredRules.map((rule) => {
                const calcConfig = getCalculationConfig(rule.calculation_type);
                const CalcIcon = calcConfig.icon;
                return (
                  <tr key={rule.id} className="hover:bg-zinc-900/30 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <span className="font-medium text-white">{rule.rule_name}</span>
                        <p className="text-xs text-[#93AE9F] mt-1">{formatCondition(rule)}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge className="bg-emerald-500/20 text-emerald-400 border-0">{rule.object_type}</Badge>
                    </td>
                    <td className="px-6 py-4">
                      <Badge className={cn(
                        'border-0',
                        calcConfig.color === 'emerald' ? 'bg-emerald-500/20 text-emerald-400' :
                        calcConfig.color === 'red' ? 'bg-red-500/20 text-red-400' :
                        'bg-blue-500/20 text-blue-400'
                      )}>
                        <CalcIcon className="w-3 h-3 mr-1" />
                        {calcConfig.label} {rule.points}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleRuleStatus(rule)}
                        className="flex items-center gap-2"
                      >
                        {rule.is_active ? (
                          <Badge className="bg-emerald-500/20 text-emerald-400 border-0 cursor-pointer hover:bg-emerald-500/30">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Active
                          </Badge>
                        ) : (
                          <Badge className="bg-zinc-600/50 text-zinc-400 border-0 cursor-pointer hover:bg-zinc-600">
                            <XCircle className="w-3 h-3 mr-1" />
                            Inactive
                          </Badge>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-zinc-900 border-zinc-700">
                          <DropdownMenuItem
                            onClick={() => openEditModal(rule)}
                            className="text-zinc-300 hover:text-white focus:text-white"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDelete(rule.id)}
                            className="text-red-400 hover:text-red-300 focus:text-red-300"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      
      <div className="mt-4 text-sm text-[#93AE9F]">
        {filteredRules.length} rules • {filteredRules.filter(r => r.is_active).length} active
      </div>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-400 mb-1">About Scoring Rules</h4>
            <p className="text-sm text-blue-300/80">
              Scoring rules help track engagement across the platform. Points are calculated based on 
              user actions, field changes, and events. Use scores to identify high-risk agencies, 
              prioritize support tickets, or reward active users.
            </p>
          </div>
        </div>
      </div>

      {/* Add/Edit Rule Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Scoring Rule' : 'Add Scoring Rule'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Rule Name *</label>
              <Input
                value={formData.rule_name}
                onChange={(e) => setFormData({ ...formData, rule_name: e.target.value })}
                placeholder="e.g., Agency login activity"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Object Type *</label>
              <Select
                value={formData.object_type}
                onValueChange={(v) => setFormData({ ...formData, object_type: v })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Select object type" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {objects.filter(o => o.is_active).map((obj) => (
                    <SelectItem key={obj.id} value={obj.name}>{obj.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Condition Type</label>
              <Select
                value={formData.condition_type}
                onValueChange={(v) => setFormData({ ...formData, condition_type: v })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {CONDITION_TYPES.map((cond) => (
                    <SelectItem key={cond.value} value={cond.value}>{cond.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Condition Config */}
            {['field_equals', 'field_not_equals', 'field_contains'].includes(formData.condition_type) && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Field Name</label>
                  <Input
                    value={formData.condition_config.field || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      condition_config: { ...formData.condition_config, field: e.target.value }
                    })}
                    placeholder="e.g., status"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Value</label>
                  <Input
                    value={formData.condition_config.value || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      condition_config: { ...formData.condition_config, value: e.target.value }
                    })}
                    placeholder="e.g., active"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Calculation</label>
                <Select
                  value={formData.calculation_type}
                  onValueChange={(v) => setFormData({ ...formData, calculation_type: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {CALCULATION_TYPES.map((calc) => (
                      <SelectItem key={calc.value} value={calc.value}>{calc.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Points</label>
                <Input
                  type="number"
                  min="1"
                  value={formData.points}
                  onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 1 })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between pt-2">
              <div>
                <label className="text-sm font-medium text-white">Active</label>
                <p className="text-xs text-zinc-500">Enable this rule to start calculating scores</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddModal(false);
                setEditingRule(null);
                resetForm();
              }}
              className="border-zinc-600 text-zinc-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {editingRule ? 'Save Changes' : 'Create Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformManageScoringPage;
